/*
 * Upgrade — the deal: one shared state for the whole loop.
 * ---------------------------------------------------------------------------
 * The store has three doors (buy / sell / trade-in) but they were three dead
 * ends: pick a phone, go to trade-in, and you were asked to pick a phone
 * again. This module is the thing that was missing — one object, held for the
 * session, that both halves of the loop read and write:
 *
 *   target   — the phone they want          (set on the product page)
 *   tradein  — the phone they already own   (set by trade-in or sell)
 *
 * As soon as BOTH exist, a sticky bar follows them across the store showing
 * the estimated balance. It survives navigation, so the loop closes from
 * either direction: shop -> trade-in, or sell -> shop.
 *
 * Money rule (unchanged): the trade-up bonus is the standing online discount
 * REDIRECTED, capped — never stacked on top of it.
 *
 * Shape of the sum (21 Sep 2026). The bonus is ADDED TO THE VALUE OF THE
 * CUSTOMER'S PHONE, not netted off the price. That is the whole reason it
 * exists: selling the phone outright pays its value in cash, trading it in
 * pays its value PLUS the bonus as credit.
 *   total credit       = your phone's value + trade-up bonus
 *   estimated balance  = target price − total credit
 *
 * TWO COMPONENTS, ONLY ONE OF THEM SOFT:
 *   - your phone's value is an ESTIMATE, always. It is settled at the physical
 *     inspection, so that line, and only that line, carries the caveat.
 *   - the trade-up bonus is GUARANTEED. It is a function of the device being
 *     bought and nothing else — not the condition, battery, grade or age of
 *     the phone being traded in — so it is known with certainty the moment a
 *     device is picked. It is never called approximate, indicative, "up to"
 *     or subject to inspection anywhere in this file.
 *
 * Ruling R3 — one source for every number. The bonus percentage and cap are
 * NOT written here. They are read from the published data file's meta block
 * (meta.bonus_pct / meta.bonus_cap, falling back to the discount_pct /
 * discount_cap the same block already publishes). Until that file has been
 * read, bonusFor() and totals() return null and nothing renders: a blank bar
 * is correct, a bar built on a guessed rate is not.
 *
 * Ruling R1 — the credit is an ESTIMATE and is never applied to the cart.
 * Payment is on delivery (cash, or card on the machine the delivery person
 * carries), and the phone's value is only known once it has been checked.
 * This module therefore:
 *   - writes no discount and never lowers a price, ever;
 *   - labels its total "Estimated balance" — estimated because the phone's
 *     value feeds it, NOT because the bonus might change;
 *   - never renders on the cart or checkout routes, where it would sit over
 *     the real subtotal quoting a smaller number (BLK-3).
 *
 * 28 Sep 2026 (LOG-021): a trade-in ends in a real ORDER for the new phone,
 * not a booking. The person who delivers it checks the old phone at the door
 * and confirms its value; the customer pays only the difference, or is paid
 * the surplus, or keeps their phone and pays the full price. So the trade-in
 * now travels WITH the order, as notes on the new phone's cart line
 * (orderProperties below): shown under the phone in the cart, at checkout and
 * in the order emails, so whoever delivers knows exactly what was quoted. The
 * notes carry the estimate; they never change the price. A sell, where no
 * phone is bought, is still a booked visit (sections/upgrade-book.liquid).
 *
 * Honesty rule: a trade-in value is an estimate until the phone is inspected.
 * Nothing here ever calls it final.
 */
(function () {
  'use strict';
  /* ---- ARABIC PHASE 2, 4 Oct 2026 (LOG-043; 2026-10-04_arabic/phase2-cart.md)
     Every customer word the BAR and its messages print is in EN below,
     exactly as it was written inline before, read through T() / TN(). An
     English page has no dictionary, so T() returns EN's text and the English
     output is identical by construction (tests/totals.test.js and the other
     suites run this file with no window.UpgradeI18n at all). On an Arabic
     page T() reads window.UpgradeI18n, falling back to EN per key.
     tools/build-i18n.js reads this table ("area.name" is the locale key
     upgrade.js.area.name_html; a { one, other } entry is a plural).
     THE ORDER NOTES ARE NOT HERE AND NEVER PASS THROUGH T(): orderProperties()
     and quoteDate() below write English DATA onto the cart line -- Flow F1
     and the Payfy rule key on it (LOG-042, LOG-031), the person at the door
     and AX read it -- with the fixed English formatter egp(), in every
     language. tools/build-i18n.js --check refuses a T(), TN(), fig(),
     money() or I18N inside those functions. API.egp stays that English
     formatter too: sections/upgrade-book.liquid puts it into the staff
     email. Display figures go through fig() / money() instead. */
  var EN = {
    'common.grade_g': 'Gold',
    'common.grade_p': 'Platinum',
    'common.grade_d': 'Diamond',
    'common.money': '{amount} EGP',
    'common.currency': 'EGP',
    'deal.bar_label': 'Your trade-up deal',
    'deal.cancel_label': 'Cancel this trade-in',
    'deal.details': 'Details',
    'deal.hide_details': 'Hide details',
    'deal.your_device': 'Your {device}',
    'deal.bonus_guaranteed': 'Bonus, guaranteed',
    'deal.estimated_balance': 'Estimated balance',
    'deal.covered_full': 'Your credit covers this device in full – we pay you the difference at delivery',
    'deal.covered_exact': 'Your credit covers this device in full. Nothing to pay at delivery, once your phone is checked.',
    'deal.value_confirmed': 'Phone value confirmed when we check it at delivery',
    'deal.valu_terms': '{amount} / month for {months} · no interest, no fees · subject to the partner\'s approval',
    'deal.valu_terms_topup': '{topup} at delivery, then {amount} / month for {months} · no interest, no fees · subject to the partner\'s approval',
    'deal.valu_terms_about': 'about {amount} / month for {months} · no interest, no fees · subject to the partner\'s approval',
    'deal.valu_line': 'or in instalments: {terms}',
    'deal.valu_months': { one: '{count} month', other: '{count} months' },
    'deal.order_cta': 'Order with trade-in',
    'deal.choose_phone': 'Choose your phone',
    'deal.adding': 'Adding…',
    'deal.view_cart': 'View cart',
    'deal.order_already': 'Your cart already has a phone with a trade-in. One trade-in goes with one phone: check out that order, or remove it from the cart first.',
    'deal.order_failed': 'We could not add that to your cart just now. Please try again in a moment, or call {hotline}.',
    'deal.cancelled': 'Trade-in cancelled.',
    'deal.announce_covered': 'Trade-up deal updated. Your credit covers {device} in full – we pay you the difference at delivery. Your phone’s value is confirmed when we check it at delivery.',
    'deal.announce_covered_exact': 'Trade-up deal updated. Your credit covers {device} in full. Nothing to pay at delivery, once your phone is checked.',
    'deal.announce_balance': 'Trade-up deal updated. Estimated balance on {device}, {amount}. Your phone’s value is an estimate confirmed when we check it at delivery. The trade-up bonus is guaranteed.',
    /* ARABIC PORT pA, 7 Oct 2026 (2026-10-04_arabic/port-2026-10-07/pA.md): the
       English build's new words (Lynkrs T2 PG-TradeIn-4: the bar's link names
       the phone). deal.covered_full and deal.announce_covered above carry
       the batch-2 en dash (D2). */
    'deal.view_device': 'View {device}'
  };
  var I18N = (typeof window !== 'undefined' && window.UpgradeI18n) || null;
  /* ---- EDIT EVERYTHING, 7 Oct 2026 (Omar: "my content team needs to be able
     to edit anything"; 2026-10-06_lynkrs-tech-fixes/edit-everything-7oct/
     build/tradein.md). The bar's words are settings of
     sections/upgrade-deal-bar.liquid ("Trade-up deal bar words", in the
     footer group), printed as one inert tag,
     <script type="application/json" data-ug-deal-words>, keyed by the EN
     key. OWN() hands T() the section's word only when it has something in
     it, its {placeholders} are exactly EN's, its digits are EN's, and, on a
     translated page, it is not EN's own text (an untranslated setting
     reaches /ar as the English value; the dictionary's Arabic shows
     instead, as before). Otherwise T() is what it was. The words are plain
     text, like EN's here: every sink escapes them (esc()) or sets them as
     text. The ORDER NOTES never pass through this: orderProperties() and
     quoteDate() stay English data (LOG-031, LOG-042). With no section on
     the page, nothing changes. */
  var OWN_WORDS = readOwnWords();
  function readOwnWords() {
    try {
      if (typeof document === 'undefined' || !document || typeof document.querySelector !== 'function') { return {}; }
      var el = document.querySelector('script[data-ug-deal-words]');
      if (!el) { return {}; }
      var o = JSON.parse(el.textContent || '{}');
      return (o && typeof o === 'object') ? o : {};
    } catch (e) { return {}; }
  }
  function placeholdersOf(s) {
    var seen = {};
    (String(s).match(/\{\w+\}/g) || []).forEach(function (p) { seen[p] = true; });
    return Object.keys(seen).sort().join(',');
  }
  function digitsOf(s) {
    return (String(s).replace(/\{\w+\}/g, '').match(/\d+/g) || []).sort().join(',');
  }
  function OWN(key) {
    var v = Object.prototype.hasOwnProperty.call(OWN_WORDS, key) ? OWN_WORDS[key] : null;
    var en = EN[key];
    if (typeof v !== 'string' || !/\S/.test(v) || typeof en !== 'string') { return null; }
    if (placeholdersOf(v) !== placeholdersOf(en) || digitsOf(v) !== digitsOf(en)) { return null; }
    if (I18N && v === en) { return null; }
    // FIX PASS, 7 Oct 2026 (fixes/FIXES.md D27): on a translated page a field is used only when it carries
    // Arabic script, or the dictionary has no such key; until its Arabic is registered the dictionary shows.
    if (I18N && !/[\u0600-\u06FF]/.test(v)) {
      try { if (I18N.has(key)) { return null; } } catch (eA) { return null; }
    }
    return v;
  }
  // {name} -> value, each value isolated on a right-to-left page (once).
  function fillOwn(str, vars) {
    return String(str).replace(/\{(\w+)\}/g, function (whole, name) {
      return (vars && Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== null && vars[name] !== undefined)
        ? isolateText(vars[name]) : whole;
    });
  }
  /* The three sentences the trade-in summary prints beside this bar, byte for
     byte (assets/upgrade-tradein.js dealSays()): this section's word, or
     null, and then the trade-in page keeps its own. */
  var SAY = {
    estimated_balance: 'deal.estimated_balance',
    covered_full: 'deal.covered_full',
    covered_exact: 'deal.covered_exact'
  };
  function say(name) {
    return Object.prototype.hasOwnProperty.call(SAY, name) ? OWN(SAY[name]) : null;
  }
  // {name} -> value; an unknown {name} is left as it is.
  function fill(str, vars) {
    return String(str == null ? '' : str).replace(/\{(\w+)\}/g, function (whole, name) {
      return (vars && Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== null && vars[name] !== undefined)
        ? String(vars[name]) : whole;
    });
  }
  // The section's word (edit everything), else the dictionary's text when it
  // has the key, else EN's. Never throws.
  function T(key, vars) {
    var own = OWN(key);
    if (own !== null) { return fillOwn(own, vars); }
    if (I18N) {
      try {
        if (I18N.has(key)) {
          var tr = I18N.t(key, vars);
          if (typeof tr === 'string') { return tr; }
        }
      } catch (e) { /* fall back to English */ }
    }
    return fill(EN[key], vars);
  }
  // Plurals: EN[key] is { one, other } (English); the dictionary carries
  // key_zero ... key_other and picks by the page language's plural rules.
  function TN(key, n, vars) {
    var v = {};
    Object.keys(vars || {}).forEach(function (k) { v[k] = vars[k]; });
    if (v.count === undefined) { v.count = n; }
    if (I18N) {
      try {
        if (I18N.has(key + '_other')) {
          var tr = I18N.plural(key, n, v);
          if (typeof tr === 'string') { return tr; }
        }
      } catch (e) { /* fall back to English */ }
    }
    var forms = EN[key];
    if (forms && typeof forms === 'object') { return fill(n === 1 ? forms.one : forms.other, v); }
    return fill(forms, v);
  }
  function rtl() {
    try { return !!(I18N && typeof I18N.dir === 'function' && I18N.dir() === 'rtl'); } catch (e) { return false; }
  }
  // Bidi isolation of a value set as text, on right-to-left pages only.
  // English: the value, untouched.
  function isolateText(value) {
    var str = String(value == null ? '' : value);
    try {
      if (rtl() && typeof I18N.isolate === 'function') { return I18N.isolate(str); }
    } catch (e) { /* English behaviour */ }
    return str;
  }
  // No bidi marks (U+2066-2069) in a tooltip (title=) -- 03-FIXES.md D9.
  function plainText(value) {
    return String(value == null ? '' : value).replace(/[⁦-⁩]/g, '');
  }
  // A DISPLAY figure: in the page's number locale (Western digits on Arabic,
  // LOG-043) and isolated on right-to-left pages. English: egp(n), exactly.
  // `sign` ('+' or '') goes INSIDE the isolate with the number, never outside.
  function fig(n, sign) {
    var s = sign || '';
    if (I18N && typeof I18N.num === 'function') {
      var d = null;
      try { d = I18N.num(Math.round(n || 0)); } catch (e) { d = null; }
      if (typeof d === 'string') { return isolateText(s + d); }
    }
    return s + egp(n);
  }
  // "<figure> EGP" in the page language (common.money). English: egp(n) + ' EGP'.
  function money(n) {
    return T('common.money', { amount: fig(n) });
  }
  // The merchant's bilingual fields ({ en, ar }), read in the page language.
  function pick(obj) {
    if (!obj || typeof obj !== 'object') { return ''; }
    try {
      if (I18N && typeof I18N.pick === 'function') {
        var p = I18N.pick(obj);
        if (typeof p === 'string') { return p; }
      }
    } catch (e) { /* English behaviour */ }
    return obj.en;
  }

  // The head snippet is rendered once per section, so this tag can appear
  // several times on one page. Initialise exactly once, like upgrade-core.js.
  if (window.UpgradeDeal) return;

  /* v2 since 1 Oct 2026: the battery answers moved to the counter's bands
     (96% or more / 91-95% / 90% or less / Don't know replaced above 90 /
     85-90 / below 85) and the deductions behind every valuation changed. A v1
     deal is NEVER read -- not migrated, not re-mapped -- so no answer given
     under the September bands can be priced, totalled or written onto an
     order under the new ones. It is removed on load so it cannot linger; the
     customer simply answers the questions again. */
  var KEY = 'ug.deal.v2';
  var RETIRED_KEYS = ['ug.deal.v1'];
  /* Battery answers that existed only under the September bands. A valuation
     carrying one -- the live one (s.tradein) or one parked on a cart line
     (s.attached.tradein, which reconcileCart() can bring back) -- is dropped in
     read(), never translated. Current code cannot write one into v2, so this is
     a second line behind the key bump. It cannot catch `unsure`, which kept its
     key but changed meaning (the light battery amount until 30 Sep, the heavy
     one from 1 Oct): for `unsure` the v1 -> v2 key bump is the only guard. */
  var RETIRED_BATTERY = { above90: true, '85to90': true, below85: true };
  function retiredBattery(t) {
    return !!(t && Object.prototype.hasOwnProperty.call(RETIRED_BATTERY, t.battery));
  }
  try { RETIRED_KEYS.forEach(function (k) { sessionStorage.removeItem(k); }); } catch (e) { /* private mode */ }

  // A deal is a shopping session, not a filing cabinet. sessionStorage already
  // dies with the tab; this caps the rare tab that lives for days.
  var MAX_AGE_MS = 24 * 60 * 60 * 1000;

  var listeners = [];

  // { pct: Number, cap: Number } once the published data file has been read,
  // null before that. Never seeded with a default — see R3 above.
  var rates = null;

  /* ---- the saved estimate (Lynkrs PG-TradeIn-3 / PSY-Endowment, batch 2,
     W1a, 6 Oct 2026) ---------------------------------------------------------
     The deal above lives in sessionStorage and dies with the tab, so a
     customer who valued a phone yesterday, or in another tab, was asked the
     questions again. The ANSWERS of the last valuation are now also kept in
     localStorage, under SAVED_KEY, with the figure they produced and the day
     they were first given. sessionStorage (KEY) stays the current session's
     source; this record is only ever READ by the trade-in page
     (assets/upgrade-tradein.js restoreFromSaved()), which runs the answers
     through its own quote engine again and shows a valuation only when the
     engine gives exactly the saved figure. The saved figure itself is never
     shown, totalled or written onto an order.

     The honest expiry, whichever comes first:
       - the price list changes: the record carries the data file's price
         month (meta.effective, handed over in setRates()) and is dropped the
         moment the file on the page names another one. A figure that no longer
         matches the engine for the same answers is dropped too (the trade-in
         page does that), so a price edit inside the same month is caught;
       - 7 days after the answers were first given (SAVED_MAX_AGE_MS). Saving
         the same answers and the same figure again keeps the first day, so
         visiting does not extend it;
       - "Start again", a new brand or model, a refused quote, the bar's x, an
         order with the trade-in on it, or a completed checkout
         (clearTradein() / clear() / order()).
     What is stored: the phone's model code, storage, the condition, battery
     and fault answers, the figure and two dates. No name, no number, no
     address, nothing that identifies a person. No bonus is stored: it is
     worked out from the phone being bought, at the moment it is chosen.
     Who keeps one (fix round, 6 Oct 2026): only a caller that says so on its
     own page, setTradein(t, { keep: true }). Today that is the trade-in page
     alone, which tells the customer in the same breath (its kept line). Any
     other caller -- the sell page -- keeps nothing on the device, because
     nothing there says so; its valuation lives in the session as before, and
     a saved estimate with DIFFERENT answers is retired (retireEstimate()), so
     the device never offers back answers the customer has since changed.
     A record that fails any check (shape, dates, a retired battery answer, the
     price month) is removed, not repaired. Storage that is blocked or missing
     (a private window, the test harnesses) simply means nothing is saved. */
  var SAVED_KEY = 'ug.estimate.v1';
  var SAVED_DAYS = 7;
  var SAVED_MAX_AGE_MS = SAVED_DAYS * 24 * 60 * 60 * 1000;
  var SAVED_SKEW_MS = 5 * 60 * 1000;   // a clock a few minutes ahead is not a forged date
  var dataVersion = null;              // meta.effective of the data file on this page, once handed over

  var state = read();

  function read() {
    try {
      var raw = sessionStorage.getItem(KEY);
      var s = raw ? JSON.parse(raw) : {};
      if (!s || typeof s !== 'object') return {};
      if (s.updated && (Date.now() - s.updated) > MAX_AGE_MS) return {};
      // A valuation older than a day is not carried, however recently the deal
      // was touched: a quote is timed from when it was made (28 Sep audit).
      if (s.tradein && typeof s.tradein.valuedAt === 'number' && (Date.now() - s.tradein.valuedAt) > MAX_AGE_MS) delete s.tradein;
      // Restore guard (1 Oct 2026): a valuation answered under the retired
      // September battery bands is not carried -- see KEY above. The parked
      // copy loses it too, so reconcileCart() cannot restore it (review fix);
      // the rest of the parked record (variantId, cartToken) stays, so the
      // used-cart logic there still works.
      if (retiredBattery(s.tradein)) delete s.tradein;
      if (s.attached && typeof s.attached === 'object' && retiredBattery(s.attached.tradein)) delete s.attached.tradein;
      return s;
    } catch (e) { return {}; }
  }

  function write() {
    state.updated = Date.now();
    try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* private mode */ }
    announce();
  }

  function announce() {
    listeners.forEach(function (fn) { try { fn(state); } catch (e) {} });
    render();
  }

  /* The saved estimate's storage. See SAVED_KEY above for the rules. */
  function localStore() {
    try {
      var ls = (typeof window !== 'undefined') ? window.localStorage : null;
      return (ls && typeof ls.getItem === 'function') ? ls : null;
    } catch (e) { return null; }   // a browser blocking site data throws on the getter
  }

  function forgetEstimate() {
    var ls = localStore();
    if (!ls) return;
    try { ls.removeItem(SAVED_KEY); } catch (e) { /* storage blocked */ }
  }

  function estimateShapeOk(r, now) {
    return !!(r && typeof r === 'object' && r.v === 1 &&
      typeof r.series === 'string' && r.series !== '' &&
      typeof r.storage === 'string' && r.storage !== '' &&
      typeof r.condition === 'string' && r.condition !== '' &&
      typeof r.battery === 'string' && r.battery !== '' &&
      typeof r.faulty === 'boolean' &&
      typeof r.value === 'number' && isFinite(r.value) && r.value > 0 &&
      typeof r.data === 'string' && r.data !== '' &&
      typeof r.savedAt === 'number' && isFinite(r.savedAt) &&
      r.savedAt <= now + SAVED_SKEW_MS && (now - r.savedAt) <= SAVED_MAX_AGE_MS);
  }

  // The stored record if its shape and dates hold; removed otherwise.
  function readEstimate() {
    var ls = localStore();
    if (!ls) return null;
    var raw = null;
    try { raw = ls.getItem(SAVED_KEY); } catch (e) { return null; }
    if (!raw) return null;
    var r = null;
    try { r = JSON.parse(raw); } catch (e) { r = null; }
    if (!estimateShapeOk(r, Date.now()) || retiredBattery(r)) { forgetEstimate(); return null; }
    return r;
  }

  /* The saved answers, for the trade-in page to price again, or null. null
     too while this page has not said which price list it carries: without
     that, nobody can tell whether the record has expired. */
  function savedEstimate() {
    if (!dataVersion) return null;
    var r = readEstimate();
    if (!r) return null;
    if (r.data !== dataVersion) { forgetEstimate(); return null; }   // the price list changed
    return {
      series: r.series, storage: r.storage, condition: r.condition, battery: r.battery,
      faulty: r.faulty, value: r.value, savedAt: r.savedAt, data: r.data
    };
  }

  function saveEstimate(t) {
    var ls = localStore();
    if (!ls || !dataVersion || !t || typeof t !== 'object') return;
    if (typeof t.series !== 'string' || !t.series || typeof t.storage !== 'string' || !t.storage) return;
    if (typeof t.condition !== 'string' || typeof t.battery !== 'string' || typeof t.faulty !== 'boolean') return;
    if (typeof t.value !== 'number' || !isFinite(t.value) || !(t.value > 0) || retiredBattery(t)) return;
    var rec = {
      v: 1, series: t.series, storage: t.storage, condition: t.condition, battery: t.battery,
      faulty: t.faulty, value: t.value, data: dataVersion, savedAt: Date.now()
    };
    var old = readEstimate();
    if (old && old.data === rec.data && old.value === rec.value && old.series === rec.series &&
        old.storage === rec.storage && old.condition === rec.condition && old.battery === rec.battery &&
        old.faulty === rec.faulty) {
      rec.savedAt = old.savedAt;   // the same estimate again: its first day stands
    }
    try { ls.setItem(SAVED_KEY, JSON.stringify(rec)); } catch (e) { /* storage full or blocked */ }
  }

  /* A valuation that is not kept (no { keep: true }) still makes a saved one
     with other answers out of date: it is forgotten. The same answers and
     figure leave it standing. */
  function retireEstimate(t) {
    var old = readEstimate();
    if (!old) return;
    if (!t || typeof t !== 'object' || old.series !== t.series || old.storage !== t.storage ||
        old.condition !== t.condition || old.battery !== t.battery || old.faulty !== t.faulty ||
        old.value !== t.value) {
      forgetEstimate();
    }
  }

  function egp(n) {
    return new Intl.NumberFormat('en-EG', { maximumFractionDigits: 0 }).format(Math.round(n || 0));
  }

  /* ---- the published bonus rate (R3) -------------------------------------- */

  /* Accepts the data file's meta block and returns true if it carried a usable
     rate. Exposed as UpgradeDeal.setRates() so a page that has ALREADY fetched
     upgrade-tradein-data.json can hand the meta straight over instead of
     making this module fetch it a second time. */
  function setRates(meta) {
    if (!meta || typeof meta !== 'object') return false;
    // Lynkrs PG-TradeIn-3 (batch 2, W1a): the price list a saved estimate is keyed to.
    if (typeof meta.effective === 'string' && meta.effective) dataVersion = meta.effective;
    var pct = (typeof meta.bonus_pct === 'number') ? meta.bonus_pct
            : (typeof meta.discount_pct === 'number') ? meta.discount_pct : null;
    var cap = (typeof meta.bonus_cap === 'number') ? meta.bonus_cap
            : (typeof meta.discount_cap === 'number') ? meta.discount_cap : null;
    if (pct === null || cap === null) return false;
    rates = { pct: pct, cap: cap };
    announce();
    return true;
  }

  /* ---- the offer's own bonus rate (LOG-029, 29 Sep 2026) ------------------
     Omar, 29 Sep 2026: "the trade-up bonus will applies during offers, but
     will be probably less than 10% ... but this is for another time when we
     do a promotion the goal is exposure and volume not profitability". The
     offer record (Theme settings > Upgrade > Offers) carries two OPTIONAL
     fields, ug_offer_bonus_pct / ug_offer_bonus_cap, published on
     window.UpgradeFlags.offer as bonusPct (whole percent) / bonusCap (whole
     EGP) by layout/theme.liquid. Blank means "the standing rate" (`rates`,
     R3 above) -- LOG-029: "By default it is today's 10% / 4,000".

     A phone is only charged the offer's rate while BOTH:
       - an offer is genuinely running right now -- window.UpgradeOffer.
         windowOpen() (29 Sep 2026, review fix), NOT the same check the offer
         bar/badge/countdown use. Those call active(), which ALSO fails
         closed on every display field (name, what, terms URL) because a
         customer-facing sentence must never render broken. windowOpen()
         checks only the merchant switch, the offer kind and the Cairo-time
         window -- so a blank Arabic offer name (Arabic is paused site-wide,
         LOG-016) cannot silently revert the BONUS to the standing rate while
         the PRICE keeps charging the reduced figure; and
       - the price map confirms THIS SKU is one the offer covers --
         window.UpgradePrices.isOnOffer(sku), independent of whether a "was"
         is shown (see assets/upgrade-offer.js's forSku(), 29 Sep 2026). A
         promotion is "selected phones" (LOG-026), not the whole catalogue,
         so a phone not on offer keeps the standing rate even while a
         promotion elsewhere on the site is running.
     Nothing here is cached: both checks and the fields themselves are read
     fresh on every call. "Guaranteed" means guaranteed at whatever rate
     applies at the moment this function runs, never frozen at a rate first
     quoted on an earlier screen.

     WHAT THIS DOES NOT COVER (review finding, 29 Sep 2026; tracked, not
     fixed here -- needs an Omar/agbaje-trust ruling on which figure wins).
     orderProperties() below calls bonusFor() ONCE, when the phone is added
     to the cart, and writes the result into the cart line's properties as
     plain text. Shopify then carries that text unchanged through checkout:
     nothing re-runs this function at the order's real completion. So a
     quote is fixed at add-to-cart time, not "at the order date" -- if an
     offer starts, ends, or has its rate edited between add-to-cart and
     checkout, the note on the line does not follow it, and "guaranteed"
     describes the rate that applied when the phone went into the cart, not
     necessarily the rate Ibrahim's offer record states by the time the
     order is placed. (This paragraph corrects an earlier, wrong claim that
     the note itself "re-reads the rate in force on the order date" --  that
     re-read only happens for a FRESH call, e.g. revisiting the trade-in
     page before adding to cart; a line already sitting in the cart is not a
     fresh call.) */
  function offerRecord() {
    var f = (window.UpgradeFlags && typeof window.UpgradeFlags === 'object') ? window.UpgradeFlags : null;
    var o = f && f.offer;
    return (o && typeof o === 'object') ? o : null;
  }

  /* 29 Sep 2026, review fix (should-fix): reads UpgradeOffer.windowOpen(),
     NOT .active(). active() also fails closed on every DISPLAY field (name,
     what, terms URL) -- correct for a customer-facing badge, wrong here.
     Arabic is paused site-wide (LOG-016), so an offer's Arabic name is
     routinely blank; before this fix that alone made active() false and
     silently reverted the bonus to the STANDING rate while the price map
     kept charging the REDUCED price -- the money moved, this check did not
     follow it. windowOpen() answers only "is a real price cut in effect
     right now", which is the one fact the bonus rate actually depends on. */
  function offerActiveNow() {
    var api = window.UpgradeOffer;
    if (!api || typeof api.windowOpen !== 'function') return false;
    try { return api.windowOpen() === true; } catch (e) { return false; }
  }

  function skuOnOffer(sku) {
    if (!sku) return false;
    var api = window.UpgradePrices;
    if (!api || typeof api.isOnOffer !== 'function') return false;
    try { return api.isOnOffer(sku) === true; } catch (e) { return false; }
  }

  /* console.warn, once per distinct message, so a merchant typo in the
     theme editor (a bonus rate that cannot mean what was intended) shows up
     to whoever is watching the console without spamming it on every render
     this module re-checks the offer record (bonusFor() is called many times
     a page). 29 Sep 2026, review fix. */
  var warned = {};
  function warnOnce(msg) {
    if (warned[msg]) return;
    warned[msg] = true;
    if (typeof console !== 'undefined' && typeof console.warn === 'function') console.warn('[upgrade-deal] ' + msg);
  }

  /* {pct, cap} to charge THIS sku right now, or null when no offer rate
     applies (the caller must then fall back to `rates`, the standing rate).
     `sku` is optional: callers that cannot name one (a price probed while
     browsing with no device pinned) simply never get the offer rate, which
     is the safe direction -- the standing rate is never wrong, only
     possibly more generous than a live promotion intends.

     29 Sep 2026, review fix (should-fix, finding 19). A field that IS
     PRESENT (a number, not blank) but out of range -- 0, negative, or above
     100 for the percent -- used to be treated exactly like a BLANK field:
     silently fall back to the standing value for that one field only, no
     signal anywhere. Blank and "typed something that cannot be a rate" are
     different merchant states and must not read the same. A present-but-
     invalid field now withdraws the WHOLE offer rate (both pct and cap fall
     back to the standing pair together, never a mix of one offer field with
     one standing field) and warns once, so a promotion is never quietly run
     at a rate nobody chose. Genuinely blank fields (config/settings_schema
     .json's "leave blank to keep the standing rate") are unaffected. */
  function offerRateFor(sku) {
    var o = offerRecord();
    if (!o || o.enabled !== true) return null;
    if (!offerActiveNow()) return null;
    if (!skuOnOffer(sku)) return null;

    var pctRaw = o.bonusPct, capRaw = o.bonusCap;
    var pctGiven = (typeof pctRaw === 'number' && isFinite(pctRaw));
    var capGiven = (typeof capRaw === 'number' && isFinite(capRaw));

    if (pctGiven && !(pctRaw > 0 && pctRaw <= 100)) {
      warnOnce('Trade-up bonus rate during this offer (' + pctRaw + '%) is out of range (0,100] -- ' +
        'the offer rate is ignored for every phone until this is fixed, and the standing rate applies.');
      return null;
    }
    if (capGiven && !(capRaw > 0)) {
      warnOnce('Trade-up bonus cap during this offer (' + capRaw + ' EGP) is not a positive amount -- ' +
        'the offer rate is ignored for every phone until this is fixed, and the standing rate applies.');
      return null;
    }

    var pct = pctGiven ? (pctRaw / 100) : (rates ? rates.pct : null);
    var cap = capGiven ? capRaw : (rates ? rates.cap : null);
    if (pct === null || cap === null) return null;
    return { pct: pct, cap: cap };
  }

  /* {pct, cap} actually in force for THIS sku right now -- the offer's own
     rate when one applies, the standing rate otherwise, or null before the
     standing rate has even been published (R3). 29 Sep 2026, review fix
     (should-fix, findings 5/13): exposed so a surface that STATES the rate
     in words (assets/upgrade-sell.js's trade-in bridge explainer, "the
     bonus is N% of that device's price") can say the number that is
     actually about to be charged for the device on screen, rather than
     always quoting the standing rate while the guaranteed figure below it
     was computed at a live promotion's different rate. Read-only: does not
     change what bonusFor()/offerRateFor() do. */
  function rateFor(sku) {
    var r = offerRateFor(sku) || rates;
    return r ? { pct: r.pct, cap: r.cap } : null;
  }

  /* The device's own SKU, for the offer-rate lookup above -- built the same
     way livePriceFor() reads a target, from window.UpgradePrices.skuFor(),
     so this agrees with the price map's own idea of what SKU a target is. */
  function targetSku(tg) {
    if (!tg || typeof tg.code !== 'string' || !tg.code) return null;
    if (tg.grade !== 'g' && tg.grade !== 'p' && tg.grade !== 'd') return null;
    var api = window.UpgradePrices;
    if (!api || typeof api.skuFor !== 'function') return null;
    try { return api.skuFor(tg.code, tg.grade) || null; } catch (e) { return null; }
  }

  /* null — not a number — until the rate has been published. Callers must
     treat null as "cannot be shown yet", never as zero.
     `sku` (optional, 29 Sep 2026, LOG-029): when given, and an offer is
     live, and the price map confirms this exact SKU is on offer, the
     offer's own rate/cap is used instead of the standing one -- see
     offerRateFor() above. Omitted, or no offer rate applies, and this is
     exactly the function it always was. */
  function bonusFor(price, sku) {
    var r = offerRateFor(sku) || rates;
    if (!r) return null;
    return Math.min(Math.round((price || 0) * r.pct), r.cap);
  }

  /* THE PINNED PRICE IS A SNAPSHOT — 28 Sep 2026 (D2, promotions-and-partners
     spec, defect 2). setTarget() copies the phone's price into sessionStorage
     and it can sit there for up to MAX_AGE_MS. Prices change monthly
     (LOG-014); a tab left open across a change quoted the OLD number on the
     bar and in the order notes while Shopify's own checkout — which always
     reads the variant's live price — charged the NEW one. Two different
     totals for one order is exactly the failure trust in this category
     cannot survive.

     window.UpgradePrices (snippets/upgrade-price-map.liquid) is the live
     source of truth for the pinned SKU: `code` is the device-storage code
     every setTarget() caller already writes (upgrade-pdp.js, the trade-up
     cards in upgrade-tradein.js/upgrade-sell.js), and `grade` is the g/p/d
     key the same callers already use — colour does not affect price (see
     withColour() above), so it is not part of the lookup.

     28 Sep 2026 REVIEW FINDING (D2 follow-up). A null from getByCode() has
     two different causes the map's own contract tells apart
     (snippets/upgrade-price-map.liquid, "THE ONE RULE FOR CONSUMERS" /
     `covers()`): "not loaded yet" (fine to keep the snapshot) and "loaded,
     and there genuinely is no price" — a grade withdrawn for a colour-price
     conflict, a duplicate SKU, or a device pulled from the collection. This
     function used to treat both the same way and fall back to the stale
     snapshot in both, which is exactly the "last price the surface happened
     to be holding" the map's contract forbids on a cash-on-delivery order.
     It now returns the UNPRICED sentinel for the second case, so
     syncedTarget() can drop the pinned target instead of quoting it — the
     same rule upgrade-tradein.js's unpin() already applies ("A target with
     no live price must not stay pinned"). */
  var UNPRICED = {};

  function livePriceFor(tg) {
    if (!tg || typeof tg.code !== 'string' || !tg.code) return null;
    if (tg.grade !== 'g' && tg.grade !== 'p' && tg.grade !== 'd') return null;
    var api = window.UpgradePrices;
    if (!api || typeof api.getByCode !== 'function') return null;
    var p;
    try { p = api.getByCode(tg.code, tg.grade); } catch (e) { return null; }
    if (typeof p === 'number' && isFinite(p) && p > 0) return p;
    // p is null: ask the map whether that null is a FACT (covers()) rather
    // than a gap in what has loaded so far. covers() is the documented
    // mechanism for exactly this ("True when a null for this SKU is a fact
    // rather than a gap in what has loaded"); complete === true is the
    // coarser fallback for a minimal/older map with no covers()/skuFor().
    try {
      if (typeof api.covers === 'function' && typeof api.skuFor === 'function') {
        var sku = api.skuFor(tg.code, tg.grade);
        if (sku && api.covers(sku)) return UNPRICED;
      } else if (api.complete === true) {
        return UNPRICED;
      }
    } catch (e) { /* fall through: not knowable yet, not a confirmed absence */ }
    return null;
  }

  /* Re-reads the live price for the pinned target and corrects the SNAPSHOT
     in place when it differs — not just the one render — so a stale number
     cannot survive a page that skips this file, and so every reader of
     state.target.price, this one included, sees the truth from here on.
     Falls back to the snapshot untouched whenever the map has no answer yet
     (not mounted on this route, still walking past 50 products, or a target
     pinned before `code`/`grade` existed): a missing live price is not
     evidence the snapshot is wrong.

     28 Sep 2026 REVIEW FINDING (D2 follow-up): when livePriceFor() answers
     UNPRICED — the map has settled and confirms there is no price for this
     SKU — the pinned target is DROPPED, not kept with a stale number. Every
     reader of state.target (this one included) then sees nothing pinned,
     the same state a customer who never picked a phone is in; render() take
     it from there (the bar hides, "Choose your phone" is offered again).

     Persisted directly, not through write()/API.clearTarget(): write() calls
     announce(), which calls render(), which calls totals(), which calls
     this — a loop, and clearTarget() would re-enter it. The correction (or
     the drop) still reaches sessionStorage; it just does not re-announce a
     change that the render pass already under way is about to paint. */
  function syncedTarget() {
    var tg = state.target;
    if (!tg) return tg;
    var live = livePriceFor(tg);
    if (live === UNPRICED) {
      delete state.target;
      state.updated = Date.now();
      try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* private mode */ }
      return null;
    }
    if (live !== null && live !== tg.price) {
      tg.price = live;
      state.updated = Date.now();
      try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* private mode */ }
    }
    return tg;
  }

  /* The whole sum, in one place, so the bar, the trade-in page and the PDP
     can never quote three different numbers for the same deal.

     `credit` is the number the customer is actually being offered: what their
     phone is worth PLUS the bonus. Callers should show the two components and
     this sum rather than a single netted figure — the bonus disappears
     otherwise, and with it the reason to trade in instead of sell. `pay` is
     unchanged and is still price − value − bonus, i.e. price − credit. */
  function totals() {
    if (!state.target || !state.tradein) return null;
    var syncedTg = syncedTarget();
    // D2 follow-up: the map has confirmed this SKU has no live price and
    // syncedTarget() has already dropped the pinned target — show nothing
    // rather than a balance built on a price this site cannot stand behind.
    if (!syncedTg) return null;
    var price = syncedTg.price || 0;
    var value = state.tradein.value || 0;
    var bonus = bonusFor(price, targetSku(syncedTg));
    if (bonus === null) return null;   // rate not published yet — show nothing
    var credit = value + bonus;
    var raw = price - credit;
    return {
      price: price,
      value: value,          // estimate: settled at the inspection
      bonus: bonus,          // guaranteed: a function of `price` alone
      credit: credit,
      pay: Math.max(0, raw),
      /* ADDED 23 September 2026, and callers MUST read it.
         `pay` is still clamped, because a negative balance is not a number any
         surface can print either. What was wrong was that the clamp was the
         ONLY signal: when the credit exceeded the price, every caller printed
         a confident "0 EGP" — on screen, in the sticky bar, and into the
         contact[Deal] field emailed to Upgrade, immediately beside the line
         telling staff the bonus is guaranteed and must not be reduced.
         The trade-up rails no longer offer such a card (see tradeUpOptions()
         in upgrade-tradein.js and upgrade-sell.js), but this function can
         still be reached with a target pinned from somewhere else and a
         valuation left in session, so the condition has to be reportable
         rather than silently flattened.
         When `covered` is true there is no balance to state. Do not print
         `pay`. Say the credit covers the device and let a human settle it. */
      /* `raw === 0` -- the credit exactly meets the price -- is a covered deal
         too: there is still no balance to state, and `pay` is still 0, so a
         strict `< 0` would reinstate "Estimated balance 0 EGP" on every surface
         the moment a repricing lands on the boundary. Measured 24 Sep 2026:
         0 of 416,784 reachable pairs hit it today, so this is hardening, not a
         live defect -- but it aligns the flag with the convention the two card
         guards already use (`!(payable > 0)` in upgrade-tradein.js and
         upgrade-sell.js), and prices move weekly.
         `surplus` stays strict: an exactly-met deal has a surplus of zero, and
         the staff email states that condition in words rather than "by 0 EGP". */
      covered: raw <= 0,
      surplus: raw < 0 ? -raw : 0
    };
  }

  /* Instalments, at the point of decision.
     The client's instruction: no standalone financing page — show what they
     would pay per month where they are already looking at what they owe,
     because "the smaller the number the bigger the conversion". Upgrade has
     several instalment facilities, so this is not gated by grade.

     27 Sep 2026, Omar (punch list B11): "Remove the hidden fees and write fees
     may apply". The figure is the amount owed divided by the tenor, rounded UP
     to the whole pound -- the How to pay calculator's arithmetic
     (assets/upgrade-calc.js) -- with no add-on of any kind. It was
     amount x 1.10 / 12 rounded to the nearest 5 EGP: a 10% fee the customer
     was never shown. There is no rate parameter any more, so no caller can put
     one back. 28 Sep 2026: the deal bar and the trade-in result now print the
     valU plan instead (valuPlan below) and the booking step no longer shows a
     trade-in; monthly() stays on the API for callers that want the plain
     arithmetic. */
  var FIN = { tenor: 12 };

  function monthly(amount, tenor) {
    var t = tenor || FIN.tenor;
    if (!amount || amount <= 0 || !t) return null;
    return { months: t, perMonth: Math.ceil(amount / t) };
  }

  /* valU, 28 Sep 2026 (LOG-023). Omar: the phone being traded in pays the down
     payment, and the other 50% is paid over 12 months with no interest and no
     fees, on the card machine the delivery person carries.

     The down payment is at least half the price, so at most half is financed.
     The trade-in credit counts toward the down payment. When it is short of
     half, the customer tops up the rest at the door; when it is more than half,
     less is financed:
       cap      = min(balance, half the price, rounded down)
       topUp    = balance - financed  (financed defined below)
     These are as estimated as the balance they come from, because the phone's
     value is settled at the inspection. A covered deal has nothing to finance.
     It needs valU's approval, and every line that prints the plan says so.

     ROUNDING, CORRECTED 28 Sep 2026 (D12, promotions-and-partners spec,
     defect 12). perMonth used to be cap / 12 rounded UP, so twelve
     instalments could add up to MORE than cap -- by up to 11 EGP -- printed
     next to "no interest, no fees". Now perMonth is cap / 12 rounded DOWN,
     and the remainder (0-11 EGP) moves into topUp, paid at delivery with the
     rest of the down payment. financed is redefined as exactly
     perMonth * months (never cap itself), so:
       financed + topUp === balance,  and  perMonth * months === financed
     hold to the pound, always -- see the sweep in tests/totals.test.js. Since
     financed can now only be smaller than cap, "the down payment is at least
     half the price" (financed <= floor(price / 2)) still holds. A cap under
     one month's rounding (< 12 EGP) has nothing to finance monthly; it all
     moves to topUp and no plan is offered, same as a covered deal.

     28 Sep 2026 REVIEW FINDING, OPEN, NOT YET SIGNED OFF. This rounding fix
     has a visible side effect the original D12 change did not call out: on a
     catalogue sweep, 105 of 227 priced deals have a trade-in that already
     covers at least half the price (balance <= floor(price/2), so cap ==
     balance); of those, 103 now print an "N EGP at delivery" prefix they
     never showed before (N is the 1-11 EGP rounding remainder — see
     tests/totals.test.js's D12 sweep). Before this fix those 103 deals had
     no at-delivery amount at all. The valU CLAIM WORDS ("no interest, no
     fees", "subject to valU approval") are unchanged — only the numeric
     prefix is new — but this sits next to the valU wording this team may
     not change without sign-off (PP-10). Arithmetic correctness (the
     exact-sum invariant) requires ONE of: this prefix, or the spec's other
     option, saying "about" instead of an exact figure when the remainder is
     the only top-up. This file keeps the exact-prefix option pending
     abdelnour-payments + mott-voice + Omar's decision — see the port log.

     RESOLVED FOR THE REVIEW COPY, 28 Sep 2026: "4 EGP at delivery, then ..."
     read as a separate payment of a few pounds, which it is not. So when the
     only top-up is the rounding remainder (roundingOnly), the line says
     "about N EGP / month" and prints no at-delivery amount; when the
     customer really tops up at the door (credit short of half the price) the
     exact amount stays. The plan object itself stays exact either way.
     Wording still goes to mott-voice with the rest of the valU copy (PP-10). */
  var VALU = { share: 0.5, tenor: 12 };

  function valuPlan(price, balance) {
    if (!(price > 0) || !(balance > 0)) return null;
    var cap = Math.min(balance, Math.floor(price * VALU.share));
    if (!(cap > 0)) return null;
    var perMonth = Math.floor(cap / VALU.tenor);
    if (!(perMonth > 0)) return null;   // under 12 EGP: nothing to finance monthly
    var financed = perMonth * VALU.tenor;
    return {
      months: VALU.tenor,
      perMonth: perMonth,
      financed: financed,
      topUp: balance - financed,
      // true when the trade-in already covers at least half the price, so the
      // only top-up is the 0-11 EGP rounding remainder (see RESOLVED below)
      roundingOnly: cap === balance
    };
  }

  // The one wording of the plan. The bar prints valuLine(); the trade-in
  // result prints valuTerms() beside a "with valU" label. Both are this one
  // sentence, so no two surfaces can describe the plan differently.
  // Arabic phase 2: three whole sentences (EN deal.valu_terms*), not a lead
  // glued onto a tail, so a language can order them its own way; the months
  // are a plural (VALU.tenor is 12, so English reads "12 months" as before).
  function valuTerms(v) {
    if (!v) return '';
    var vars = { amount: money(v.perMonth), months: TN('deal.valu_months', v.months) };
    if (v.topUp > 0 && v.roundingOnly) return T('deal.valu_terms_about', vars);
    if (v.topUp > 0) { vars.topup = money(v.topUp); return T('deal.valu_terms_topup', vars); }
    return T('deal.valu_terms', vars);
  }

  function valuLine(v) {
    return v ? T('deal.valu_line', { terms: valuTerms(v) }) : '';
  }

  /* THE TRADE-IN, WRITTEN ONTO THE ORDER (28 Sep 2026, LOG-021).
     Line-item properties on the new phone's cart line. Shopify shows them under
     the phone in the cart, at checkout, in the order emails and on the order in
     admin. They are what the person at the door reads, so they state the same
     split every other surface does: the phone's value is an estimate settled
     when it is checked, the bonus is guaranteed. They are notes, not money:
     nothing here changes what the line costs.
     `target` defaults to the phone pinned in the deal; the bonus is computed
     for that phone's own price. Returns null unless a valuation, a live price
     and the published bonus rate all exist. */
  var COND_TEXT = { flawless: 'Flawless', good: 'Good', fair: 'Fair', damaged: 'Damaged' };
  /* The 1 Oct 2026 answers first. The September keys are kept as a fallback
     only: no current path can hand orderProperties() one (v1 is never read and
     read() drops a valuation carrying a retired battery answer, parked or
     not), so they are unreachable today. A note ALREADY on a Shopify cart line
     is plain text written before the switch: it never passes through this map
     and keeps its old estimate until the phone is inspected at the door. */
  var BATT_TEXT = {
    '96plus': '96% or more', '91to95': '91–95%', '90orless': '90% or less', unsure: 'not known',
    above90: 'above 90%', '85to90': '85-90%', below85: 'below 85%'
  };

  function orderProperties(target) {
    var tg = target || state.target;
    var price = null;
    // D2 (see livePriceFor()/syncedTarget() above): re-read the live price
    // before writing the order notes.
    if (tg && tg === state.target) {
      // The pinned target: syncedTarget() corrects (or drops) the snapshot
      // itself and persists that to sessionStorage too. 28 Sep 2026 review:
      // when the map confirms this SKU has no price, syncedTarget() drops
      // the pinned target and returns null; tg follows it down, so the
      // `!tg` guard below writes nothing rather than a stale figure.
      tg = syncedTarget();
      if (tg) price = tg.price;
    } else if (tg) {
      // A target passed in explicitly — no caller does this today, but the
      // signature allows it. 28 Sep 2026 review: corrected on a LOCAL
      // variable only, never on the caller's own object — this function
      // does not own state that was not handed to it, and writing tg.price
      // directly here used to contradict that. A confirmed-unpriced answer
      // means there is nothing to quote for this SKU, so price stays null
      // rather than falling back to whatever the caller handed in.
      var liveForTg = livePriceFor(tg);
      if (liveForTg === UNPRICED) {
        price = null;
      } else if (typeof liveForTg === 'number') {
        price = liveForTg;
      } else {
        price = tg.price;   // not knowable yet: keep what was handed in
      }
    }
    var mine = state.tradein;
    if (!tg || !mine || typeof mine.value !== 'number') return null;
    if (typeof price !== 'number' || !isFinite(price) || !(price > 0)) return null;
    // D2-style re-read (LOG-029): the offer's own rate, if this sku is on
    // offer right now, computed fresh at order time -- not whatever was true
    // when the estimate was first quoted.
    var bonus = bonusFor(price, targetSku(tg));
    if (bonus === null) return null;   // rate not published yet -- write nothing
    var raw = price - (mine.value + bonus);

    var parts = [[mine.label, mine.storage].filter(Boolean).join(' ') || 'Your phone'];
    if (mine.condition) parts.push((COND_TEXT[mine.condition] || String(mine.condition)) + ' condition');
    if (mine.battery) parts.push('battery ' + (BATT_TEXT[mine.battery] || String(mine.battery)));
    parts.push(mine.faulty === true ? 'fault declared'
      : (mine.faulty === false ? 'no faults declared' : 'faults not stated'));

    /* 28 Sep 2026 audit; REVISED 29 Sep 2026 (LOG-028); wording corrected
       29 Sep 2026 (review fix, blocker finding -- LOG-030). The note carries
       the date the phone was valued, so whoever checks it can tell a quote
       from a later price list. Delivery is free and its cost is already in
       the phone's price (LOG-028, 29 Sep 2026: "delivery is free and is
       already included in the price of the phone"), so the amount at the
       door is exactly the figures below -- nothing here adds or names a
       delivery charge. LOG-030 records Shopify's own domestic rate ("قياسي")
       set to 0 EGP the same day (deliveryProfileUpdate, 118.0 -> 0.0,
       read back as 0.0), so the figures below are the checkout total too,
       not an estimate that a live 118 EGP shipping line would undercut.
       This rests on the decision log as the company's record of what was
       done in Shopify admin -- this file has no way to read that setting
       itself and did not query it live; if that record is ever wrong,
       correct it at the source (ashgrove-cos) before trusting this
       comment. */
    var quoted = quoteDate(mine.valuedAt);
    var p = {};
    p['Trade-in'] = parts.join(' · ');
    p['Trade-in value'] = egp(mine.value) + ' EGP, an estimate' + (quoted ? ' quoted on ' + quoted : '') +
      ': confirmed when we check your phone at delivery';
    p['Trade-up bonus'] = egp(bonus) + ' EGP, guaranteed';
    if (raw > 0) {
      p['To pay at delivery'] = 'About ' + egp(raw) + ' EGP, once your phone is checked';
    } else if (raw < 0) {
      p['We pay you at delivery'] = 'About ' + egp(-raw) + ' EGP, once your phone is checked';
    } else {
      // Wording, 29 Sep 2026 (review fix, minor finding 9): was "Nothing to
      // pay: ...". Rendered under the "To pay at delivery" label (see
      // sections/upgrade-cart.liquid's dt/dd pair), that read "To pay at
      // delivery: Nothing to pay: ..." -- "pay" twice, two colons. The label
      // already says "to pay"; the value only needs to say there is none.
      p['To pay at delivery'] = 'Nothing: your credit covers the price, once your phone is checked';
    }
    /* TAX-PAID PASS, 7 Oct 2026 (2026-10-06_lynkrs-tech-fixes/tax-paid-7oct/CHANGE-MAP.md, fifth change; B10 /
       PG-Checkout-4; LOG-042). HIDDEN line properties, added AFTER the visible lines above, which stay byte for byte
       as they were. Shopify keeps a property whose name starts with "_" on the order (Admin, the Admin API, order
       webhooks) and shows it nowhere in the cart or at checkout; sections/upgrade-cart.liquid already skips "_" keys.
       _trade_in = '1' is the fixed routing key Flow F1 tests (2026-10-04_tradein-routing/shopify-setup.md section 4),
       so a reworded or translated "Trade-in" label can never break routing. The _ti_* keys are the customer's RAW
       answers (codes, not words): staff never trust the printed "Trade-in value" (a customer can edit any line
       property through /cart/change.js, and on Grow nothing on the server can stop it); they re-enter these answers
       in /pages/trade-in to recompute it, and a mismatch with the visible figure flags tampering. No figure is
       written here: not the value, not the bonus, not a buy price. A key whose answer is missing is left out
       (Shopify drops a blank property, and order()'s same-line check compares the properties it gets back). */
    p['_trade_in'] = '1';
    if (mine.series) p['_ti_model'] = String(mine.series);
    if (mine.storage) p['_ti_storage'] = String(mine.storage);
    if (mine.condition) p['_ti_condition'] = String(mine.condition);
    if (mine.battery) p['_ti_battery'] = String(mine.battery);
    p['_ti_faulty'] = mine.faulty === true ? 'yes' : (mine.faulty === false ? 'no' : 'unknown');
    var quotedIso = isoDate(mine.valuedAt);
    if (quotedIso) p['_ti_quoted'] = quotedIso;
    return p;
  }

  // "28 Sep 2026" -- built by hand, never toLocaleDateString, whose month
  // names differ between browsers and locales.
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function quoteDate(ts) {
    if (typeof ts !== 'number' || !isFinite(ts)) return '';
    var d = new Date(ts);
    return d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
  }
  // TAX-PAID PASS, 7 Oct 2026: the same day as quoteDate(), as an ISO date ("2026-10-07") for the hidden _ti_quoted.
  function isoDate(ts) {
    if (typeof ts !== 'number' || !isFinite(ts)) return '';
    var d = new Date(ts);
    var m = d.getMonth() + 1, day = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
  }

  function routeRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  /* Arabic port (pA, 7 Oct 2026): a site path from window.UpgradeRoutes
     (snippets/upgrade-routes.liquid prints Shopify's product.url, and the
     shop as '/pages/shop') keeps the language prefix on a translated page:
     '/products/iphone-15' -> '/ar/products/iphone-15'. A path that already
     carries the prefix, an absolute or protocol-relative URL, and anything on
     an English page (routeRoot() is '/') are returned exactly as given. */
  function localHref(u) {
    var s = String(u == null ? '' : u);
    var r = routeRoot();
    if (r === '/' || s.charAt(0) !== '/' || s.charAt(1) === '/' || s.indexOf(r) === 0 || s + '/' === r) return s;
    return r + s.slice(1);
  }

  /* ORDER WITH TRADE-IN. Puts the phone they want in the cart with their
     trade-in written on it, then takes them to the cart. One implementation
     for every such button -- the bar, the trade-in result, and the product
     page's Add to cart when a valuation is live -- so the notes on the order
     cannot depend on which button was pressed.

     ONE TRADE-IN, ONE PHONE, checked against the CART, not only the session
     (28 Sep 2026 review):
       - the cart already carries a trade-in: refuse (error code 'already'),
         and the caller says so -- a second identical line would merge into
         one line at quantity 2 with one trade-in;
       - the same phone is already in the cart, one unit and no notes: the
         trade-in goes onto THAT line (cart/change.js) rather than a second
         phone being added;
       - otherwise the phone is added (cart/add.js).
     Two presses in flight share one request (the bar and the result both
     show the button on /pages/trade-in).

     The valuation is not thrown away once it travels: it is parked as
     state.attached, so it stops showing (no bar, no second attach) but comes
     back if the customer removes that line from the cart (reconcileCart()).
     Resolves once the phone is in the cart; rejects if there is nothing to
     order, the cart already has a trade-in, or Shopify refused it. */
  var inflight = null;

  function orderError(code, msg) {
    var e = new Error(msg);
    e.code = code;
    return e;
  }

  function postJson(route, body) {
    return fetch(routeRoot() + route, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw orderError('refused', 'HTTP ' + r.status);
      return r.json();
    });
  }

  function hasNotes(item) {
    var p = item && item.properties;
    if (!p || typeof p !== 'object') return false;
    return Object.keys(p).some(function (k) { return p[k] !== null && p[k] !== ''; });
  }

  function order() {
    if (inflight) return inflight;
    // D2: the live price for THIS order, not whatever was pinned when the
    // tab was opened — see livePriceFor()/syncedTarget() above.
    var tg = syncedTarget();
    var props = orderProperties(tg);
    if (!tg || !tg.variantId || !props) return Promise.reject(orderError('nothing', 'nothing to order'));
    var variantId = String(tg.variantId);
    var mine = state.tradein;
    var cartToken = null;
    inflight = fetch(routeRoot() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw orderError('refused', 'HTTP ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        cartToken = (cart && cart.token) || null;
        /* THIS trade-in already on THIS phone: a previous press whose reply
           was lost. Treat it as done rather than refusing the customer's own
           order back to them. Any other trade-in line is a refusal. */
        var same = items.some(function (it) {
          return it && it.properties && String(it.variant_id || it.id) === variantId &&
            JSON.stringify(it.properties) === JSON.stringify(props);
        });
        if (same) return { alreadyThere: true };
        if (items.some(function (it) { return it && it.properties && it.properties['Trade-in']; })) {
          throw orderError('already', 'the cart already carries a trade-in');
        }
        var line = -1;
        items.forEach(function (it, i) {
          if (line === -1 && String(it.variant_id || it.id) === variantId && it.quantity === 1 && !hasNotes(it)) line = i + 1;
        });
        return line !== -1
          ? postJson('cart/change.js', { line: line, quantity: 1, properties: props })
          : postJson('cart/add.js', { items: [{ id: tg.variantId, quantity: 1, properties: props }] });
      })
      .then(function (res) {
        inflight = null;
        state.attached = { tradein: mine, variantId: variantId, cartToken: cartToken };
        delete state.tradein;
        forgetEstimate();   // Lynkrs PG-TradeIn-3 (W1a): the estimate now travels on the order
        write();
        window.location.href = routeRoot() + 'cart';
        return res;
      }, function (e) {
        inflight = null;
        throw e;
      });
    return inflight;
  }

  // What a failed order says, one wording for every button.
  function orderFailMessage(e) {
    return (e && e.code === 'already')
      ? T('deal.order_already')
      : T('deal.order_failed', { hotline: '19759' });
  }

  /* The cart page says whether any line carries a trade-in
     (sections/upgrade-cart.liquid, data-ug-cart-tradein). If none does and a
     valuation is parked, one of two things happened:
       - the customer removed that line. The cart is the SAME cart (same
         token): the valuation comes back, so the bar and Add to cart can
         carry it again;
       - the order was placed. Shopify's checkout runs none of this theme's
         code, so nothing cleared the parked valuation, but the cart is a NEW
         one (different token): the trade-in is used, so it and the phone it
         was for are dropped. Restoring it here would offer the customer the
         same trade-in again for the phone they just bought (28 Sep audit).
     A newer valuation is never overwritten by an older parked one. Returns a
     promise, so a caller (or a test) can wait for it. */
  function reconcileCart() {
    if (!state.attached) return Promise.resolve(false);
    var el = document.querySelector('[data-ug-cart-tradein]');
    if (!el || el.getAttribute('data-ug-cart-tradein') !== 'false') return Promise.resolve(false);
    var parked = state.attached;
    return fetch(routeRoot() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (state.attached !== parked) return false;       // something newer moved it
        var sameCart = !!(cart && cart.token && parked.cartToken && cart.token === parked.cartToken);
        if (sameCart) {
          if (!state.tradein && parked.tradein) state.tradein = parked.tradein;
        } else if (state.target && String(state.target.variantId || '') === String(parked.variantId || '')) {
          delete state.target;
        }
        delete state.attached;
        write();
        return sameCart;
      }, function () { return false; });
  }

  /* WHAT A DEVICE WOULD COST THIS CUSTOMER, given the valuation they already
     have. Omar, 24 September 2026: while a trade-in is in progress the shop
     should show the difference they would pay, not only the full price.

     One helper, used by BOTH the catalogue cards and the product page, so the
     figure cannot drift between the two surfaces. `totals()` is the same
     arithmetic for the ONE device already pinned as the target; this answers it
     for an ARBITRARY price while they browse.

     The bonus is a function of the device being BOUGHT, so it is recomputed per
     price -- a cheaper phone carries a smaller bonus, and the credit is not a
     constant the caller can cache.

     THE FULL PRICE REMAINS THE PRICE. Callers must render this BESIDE the real
     figure, never instead of it: under R1 the cart charges the full amount and
     the courier collects it, and the credit is settled at inspection. A single
     netted number on a card would be the one thing this store is built not to
     do. `covered` carries the same meaning as in totals() -- no balance to
     state, so say so in words rather than printing a zero.
     `sku` (optional, 29 Sep 2026, LOG-029): passed through to bonusFor() so
     a catalogue card or PDP preview for a phone genuinely on offer shows the
     offer's own bonus rate rather than the standing one. Omitted -- e.g. a
     "From" card priced across several variants with no single SKU to name --
     and this is exactly the function it always was: the standing rate. */
  function creditFor(price, sku) {
    if (!state.tradein || typeof state.tradein.value !== 'number') return null;
    if (typeof price !== 'number' || !isFinite(price) || price <= 0) return null;
    var bonus = bonusFor(price, sku);
    if (bonus === null) return null;   // rate not published yet -- state nothing
    var credit = state.tradein.value + bonus;
    var raw = price - credit;
    return {
      value: state.tradein.value,  // estimate, settled at inspection
      bonus: bonus,                // guaranteed, fixed by the device being bought
      credit: credit,
      balance: Math.max(0, raw),
      covered: raw <= 0,
      // F6, 29 Sep 2026 (fix_now, prices-on-page P7): same convention as
      // totals()'s own surplus, above -- strict, so an EXACT match (raw === 0)
      // reads as surplus 0, not a difference owed back.
      surplus: raw < 0 ? -raw : 0
    };
  }

  /* Colour variants, ported 27 Sep 2026. Products are gaining a third option,
     "Colour", and the product page may pin it on the target as target.colour --
     the Shopify option value as the shopper sees it. Price does not vary by
     colour, so nothing that does money (totals, creditFor, bonusFor) reads it:
     it is a label, and it is OPTIONAL. A target with no `colour` key is stored
     as the very object handed over, so every surface behaves exactly as before.
     One that carries a blank or non-string colour has the key dropped (on a
     copy, never on the caller's object), so no consumer has to tell "" from
     absent. */
  function withColour(t) {
    if (!t || typeof t !== 'object' || !Object.prototype.hasOwnProperty.call(t, 'colour')) return t;
    var c = (typeof t.colour === 'string') ? t.colour.trim() : '';
    if (c && c === t.colour) return t;
    var out = {};
    Object.keys(t).forEach(function (k) { if (k !== 'colour') out[k] = t[k]; });
    if (c) out.colour = c;
    return out;
  }

  function colourOf(tg) {
    return (tg && typeof tg.colour === 'string') ? tg.colour.trim() : '';
  }

  // Arabic phase 2: the bar's device name, piece by piece (see render()).
  function namePart(s) { return s ? isolateText(s) : s; }
  function gradeName(tg) {
    if (I18N && tg && (tg.grade === 'g' || tg.grade === 'p' || tg.grade === 'd')) { return T('common.grade_' + tg.grade); }
    return tg ? tg.gradeLabel : tg;
  }

  var API = {
    get: function () { return state; },
    totals: totals,
    creditFor: creditFor,
    bonusFor: bonusFor,
    rates: function () { return rates ? { pct: rates.pct, cap: rates.cap } : null; },
    rateFor: rateFor,
    setRates: setRates,
    monthly: monthly,
    finance: FIN,
    // 28 Sep 2026 (LOG-021, LOG-023): the trade-in as an order, and valU.
    valuPlan: valuPlan,
    valuLine: valuLine,
    valuTerms: valuTerms,
    orderProperties: orderProperties,
    order: order,
    orderFailMessage: orderFailMessage,
    reconcileCart: reconcileCart,
    orderLabel: function () { return ORDER_LABEL; },
    targetHref: function (t) { return targetHref(t || state.target || {}); },
    // Colour variants, ported 27 Sep 2026: see withColour().
    setTarget: function (t) { state.target = withColour(t); write(); },
    // The valuation is stamped with when it was made (28 Sep audit): the order
    // note dates the quote, and a quote over a day old is not carried.
    setTradein: function (t, opts) {
      if (t && typeof t === 'object' && typeof t.valuedAt !== 'number') {
        var c = {};
        Object.keys(t).forEach(function (k) { c[k] = t[k]; });
        c.valuedAt = Date.now();
        t = c;
      }
      state.tradein = t;
      // Lynkrs PG-TradeIn-3 (W1a): the answers, for another tab or day -- only
      // where the page tells the customer so (opts.keep); see SAVED_KEY.
      if (opts && opts.keep === true) saveEstimate(t); else retireEstimate(t);
      write();
    },
    clearTarget: function () { delete state.target; write(); },
    clearTradein: function () { delete state.tradein; forgetEstimate(); write(); },
    clear: function () {
      state = {};
      try { sessionStorage.removeItem(KEY); } catch (e) { /* private mode */ }
      forgetEstimate();
      announce();
    },
    // Lynkrs PG-TradeIn-3 (batch 2, W1a): the saved estimate -- see SAVED_KEY.
    savedEstimate: savedEstimate,
    forgetEstimate: forgetEstimate,
    savedDays: SAVED_DAYS,
    quoteDate: quoteDate,
    // Edit everything (7 Oct 2026): the bar's own word for a sentence the trade-in summary shares, or null.
    say: say,
    on: function (fn) { listeners.push(fn); return fn; },
    egp: egp
  };
  window.UpgradeDeal = API;

  /* ---- routes ------------------------------------------------------------- */

  // Optional Shopify locale prefix: /ar, /en-eg, /fr-ca.
  var LOCALE = '(?:\\/[a-z]{2}(?:-[a-z]{2,3})?)?';

  /* BLK-3. On the cart and the checkout the bar was rendering a LOWER total
     directly over the real subtotal and the checkout button, with a CTA that
     led away from checkout. Under R1 the cart charges the full price, so a
     second, smaller number on that screen is simply a contradiction.

     Deliberately a synchronous pathname test and nothing else. Testing cart
     CONTENTS would need an async /cart.js fetch, which would let the bar paint
     and then vanish on the trade-in pages — where the bar IS the journey. */
  var SUPPRESS_RE = new RegExp(
    '^' + LOCALE + '\\/(?:cart|checkout|checkouts|orders|thank_you|wallets)(?:\\/|$)', 'i'
  );

  function suppressed() {
    return SUPPRESS_RE.test(String(location.pathname || ''));
  }

  /* The journey is over when the booking enquiry has actually posted, or when
     an order exists. Shopify bounces back to the same page with
     ?contact_posted=true once the {% form 'contact' %} in upgrade-book.liquid
     succeeds — that is the one reliable "done" signal the theme emits.

     Note what is NOT here: navigation between the trade-in page and a product
     page. That is the live journey, and clearing on it is what the previous
     build's single call site was never allowed to do. */
  /* 28 Sep 2026 audit: which journey ended. 'booking' only where a booking
     form is on the page -- the general contact page posts the same
     ?contact_posted=true and must not wipe a customer's deal -- and a booking
     is a sell, so it ends the valuation but never a trade-in already parked on
     an order in the cart. 'order' ends everything. */
  function complete() {
    var path = String(location.pathname || '');
    if (/\/thank_you(?:\/|$)/i.test(path)) return 'order';            // order status
    if (new RegExp('^' + LOCALE + '\\/orders\\/', 'i').test(path)) return 'order';
    if (/[?&]contact_posted=true/i.test(String(location.search || '')) &&
        document.querySelector('[data-ug-book]')) return 'booking';
    return false;
  }

  /* ---- the sticky summary bar -------------------------------------------- */

  var bar = null;
  var barStatus = null;      // the live region, OUTSIDE the bar (see ensureBarStatus)
  var lastBarPhrase = '';    // de-dupe: a render with the same words says nothing
  var lastTradeinSig = '';   // the trade-in leg, so a target-only change is silent
  var barWasShown = false;   // the appear edge
  var barAnnounceTimer = null;
  var primed = false;        // first render of a page load records state, says nothing

  /* THE BAR PUBLISHES ITS OWN HEIGHT. Two rails are pinned to the foot of a
     product page -- this one and .ug-sticky (upgrade-core.css section 13) --
     and this one is stacked above it on purpose (z 50 over --z-sticky 40), so
     without an offset it covered the buy bar completely at every width below
     750. Nothing may hard-code that offset: the bar is 198px tall for a normal
     deal and 229px when the credit covers the price, taller again now its
     content wraps rather than overflows, and the rem lengths in this file have
     already been globally rescaled once (see upgrade-tokens.css:32-63). So the
     real, current border-box height goes on <html> as --ug-deal-h and every
     surface that has to clear the bar reads it.
     The entrance animation translates only (ug-deal-in), so the measured height
     is stable while it plays, and env(safe-area-inset-bottom) is already inside
     .ug-deal__inner's padding, so the notch inset is inside this number too. */
  /* The bar's live region is a SIBLING of the bar, never a child of it.
     render() assigns el.innerHTML wholesale, so anything inside the bar is
     destroyed and re-inserted on every write() -- and write() runs on every
     UpgradeDeal.setTarget(), which upgrade-pdp.js calls once per updateAll(),
     i.e. once per grade click and once per arrow key. A region re-inserted with
     content in it is announced. Outside the bar, this node is created exactly
     once and only ever has its textContent set.
     It must never be `hidden` and must never move inside the bar while the bar
     is hidden: AT does not announce a live region in a hidden subtree, and then
     fires the lot the moment the subtree is revealed. */
  function ensureBarStatus() {
    if (barStatus && document.body.contains(barStatus)) return barStatus;
    barStatus = document.createElement('p');
    barStatus.className = 'ug-deal__sr';
    barStatus.setAttribute('data-ug-deal-status', '');
    barStatus.setAttribute('role', 'status');
    barStatus.setAttribute('aria-live', 'polite');
    barStatus.setAttribute('aria-atomic', 'true');
    document.body.appendChild(barStatus);
    return barStatus;
  }

  function tradeinSig() {
    var m = state.tradein;
    if (!m) return '';
    return [m.series, m.storage, m.condition, m.battery, m.value].join('|');
  }

  /* WHAT THE BAR SAYS, AND WHAT IT DELIBERATELY DOES NOT.

     It speaks when the bar APPEARS and when the TRADE-IN leg changes. It stays
     silent when only the target changed, because a target change on a product
     page is a grade or storage click that upgrade-pdp.js has just announced --
     with the new price AND the new balance -- in one sentence. Two live regions
     firing for one click is the failure this design exists to prevent, and
     setTarget() is called on every arrow key, so the alternative is a
     four-figure sum read out per keypress.
     Trade-off taken knowingly: a screen-reader user on a product page hears the
     balance from the page, not from the bar. The bar stays a labelled region
     ("Your trade-up deal") they can navigate to at any time. */
  function announceDeal(t, targetName, appeared) {
    if (!barStatus) return;
    var sig = tradeinSig();
    var changed = appeared || sig !== lastTradeinSig;
    lastTradeinSig = sig;
    if (!changed) return;

    /* PRIMED, NOT SPOKEN, on the first render of a page load.

       The deal survives in sessionStorage, so on every navigation the bar
       "appears" again without the customer having done anything -- and the
       first version of this shipped exactly that: loading any product page read
       the whole deal out unprompted. Caught in the browser, not in the tests,
       because it is a page-lifecycle behaviour rather than a state transition.

       The state above is still recorded, so the NEXT genuine change speaks
       normally. This is the same rule upgrade-pdp.js applies with _userActed:
       a live region answers actions, it does not greet you. */
    if (!primed) {
      primed = true;
      lastBarPhrase = '';
      return;
    }

    // Same words as the bar itself prints, and as upgrade-book.liquid:220.
    // If that copy changes, change this in the same commit.
    // F6, 29 Sep 2026 (fix_now, prices-on-page P7): a covered deal is now two
    // sentences, not one -- t.surplus > 0 keeps the existing "we pay you the
    // difference" wording; an EXACT match (surplus === 0) has no difference
    // to pay, so it says that instead, rather than implying money changes
    // hands at the door when it does not.
    var phrase = t.covered
      ? (t.surplus > 0
          ? T('deal.announce_covered', { device: targetName })
          : T('deal.announce_covered_exact', { device: targetName }))
      : T('deal.announce_balance', { device: targetName, amount: money(t.pay) });

    if (phrase === lastBarPhrase) return;
    lastBarPhrase = phrase;
    clearTimeout(barAnnounceTimer);
    barAnnounceTimer = setTimeout(function () { barStatus.textContent = phrase; }, 200);
  }

  function publishHeight() {
    var h = (bar && !bar.hidden) ? Math.ceil(bar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--ug-deal-h', h + 'px');
  }

  function ensureBar() {
    ensureBarStatus();
    if (bar && document.body.contains(bar)) return bar;
    bar = document.createElement('div');
    bar.className = 'ug-deal';
    bar.setAttribute('role', 'region');
    // Not "your trade-in estimate": half of what the bar shows — the bonus —
    // is not an estimate, and the label is read aloud before the figures are.
    bar.setAttribute('aria-label', T('deal.bar_label'));
    bar.hidden = true;
    document.body.appendChild(bar);
    if (typeof ResizeObserver === 'function') {
      // borderBoxSize is the untransformed layout box, so the entrance
      // animation cannot corrupt it. No feedback loop: the bar is fixed and
      // full-bleed, so nothing that reads --ug-deal-h can change its width.
      new ResizeObserver(function (entries) {
        var e = entries[0];
        var h = (e && e.borderBoxSize && e.borderBoxSize[0])
          ? Math.ceil(e.borderBoxSize[0].blockSize)
          : Math.ceil(bar.getBoundingClientRect().height);
        document.documentElement.style.setProperty('--ug-deal-h', (bar.hidden ? 0 : h) + 'px');
      }).observe(bar);
    } else if (typeof window.addEventListener === 'function') {
      // Old WebViews: re-measure on the one event that changes the wrap.
      // Guarded rather than assumed -- this module is also loaded under a
      // minimal DOM stub by tests/totals.test.js, and an unguarded call there
      // threw before the suite could run a single check.
      window.addEventListener('resize', publishHeight);
    }
    return bar;
  }

  // LF7 fix (29 Sep 2026, fix_now, audit w4k72rk5w). Tried in order: the
  // page's own trade-in CTA (whatever shape it takes on this page -- the
  // PDP's two copies, the trade-in band's, or the header nav's "Trade-in"
  // link, the one thing every page carries -- data-kind="tradein",
  // sections/upgrade-header.liquid), then the page's <h1>. Never throws on
  // a page/stub with neither: focus() is only called when a real target
  // was found, so a caller with no DOM at all (this file's own test
  // harness) just does nothing here, same as before this fix existed.
  function focusStableTargetAfterCancel() {
    var target = null;
    try {
      target = document.querySelector(
        '[data-ug-pdp-tradein-cta]:not(.is-disabled), [data-ug-pdp-sticky-tradein-cta]:not(.is-disabled), ' +
        '.ug-tradeinband__cta, [data-kind="tradein"]'
      );
    } catch (e) { target = null; }
    if (!target) {
      try { target = document.querySelector('h1'); } catch (e) { target = null; }
      if (target && !target.hasAttribute('tabindex')) { target.setAttribute('tabindex', '-1'); }
    }
    if (target && typeof target.focus === 'function') {
      try { target.focus(); } catch (e) { /* nothing more this file can do */ }
    }
  }

  function hideBar() {
    if (bar) { bar.hidden = true; bar.innerHTML = ''; }
    // html.ug-has-deal is what reserves the foot of the page for the bar, and
    // --ug-deal-h is what says how much. Left behind on /cart the class is a
    // band of dead space under the checkout button; left behind on a PDP the
    // variable would float the sticky buy bar above nothing.
    document.documentElement.classList.remove('ug-has-deal');
    document.documentElement.style.removeProperty('--ug-deal-h');
    /* Emptying a live region is itself silent, so this costs nothing and stops
       a stale balance being re-read if the bar comes back. lastBarPhrase resets
       too: a bar that reappears HAS appeared, and should say so.
       Nothing is announced on the way out from here -- the bar hides for four
       different reasons (the x, a cleared valuation after Add to cart, a target
       dropped for want of a price, a suppressed route) and only one of them is
       a cancellation. That one announces itself, at the close handler. */
    if (barStatus) { barStatus.textContent = ''; }
    lastBarPhrase = '';
    barWasShown = false;
  }

  function render() {
    // Route guard first, before the bar element is ever created.
    if (suppressed()) { hideBar(); return; }
    var appeared = !barWasShown;   // captured before anything shows

    var t = totals();

    if (!t) { ensureBar(); hideBar(); return; }

    var el = ensureBar();
    var target = state.target;
    var mine = state.tradein;
    // Colour variants, ported 27 Sep 2026: colour last, when the target carries
    // one: "iPhone 15 · Platinum · 128GB · Blue". Absent, the name is the three
    // parts it always was.
    // Arabic phase 2: on a translated page the grade is named from its key
    // (target.grade), not from target.gradeLabel -- the deal state holds
    // display text written in whichever language the customer was on when
    // they pinned it (00-PLAN.md 2.3). Model, storage and colour stay as
    // stored (Latin) and are isolated on RTL. English: exactly as before.
    var targetName = [namePart(target.label), gradeName(target), namePart(target.storage), namePart(colourOf(target))].filter(Boolean).join(' · ');
    var mineName = [mine.label, mine.storage].filter(Boolean).join(' ');

    // OBSIDIAN, 29 Sep 2026 (C8): "The Offer pill appears only through
    // UpgradeOfferUI." -- '' with the offer switch off, or with no live
    // UpgradeOfferUI, or when the target's own SKU is not on offer; the
    // bar never decides this on its own.
    var offerPillHtml = '';
    if (window.UpgradeOfferUI && typeof window.UpgradeOfferUI.forSku === 'function') {
      var dealSku = targetSku(target);
      if (dealSku) {
        var dealOffer = window.UpgradeOfferUI.forSku(dealSku, null, { available: true });
        var dealOfferName = (dealOffer && dealOffer.name) ? pick(dealOffer.name) : '';
        if (dealOfferName) {
          offerPillHtml = '<span class="ug-opill ug-opill--sm">' + esc(dealOfferName) + '</span> ';
        }
      }
    }

    /* WHAT THE BAR SHOWS, AND WHY IT IS NOT THE FULL FIVE-LINE SUM.
       The bar is a strip at the foot of every page — three legs plus the
       balance is the whole of its designed capacity (upgrade-deal.css sizes
       the page's bottom padding for exactly that). The booking summary and the
       trade-in result carry all five lines; here the one line that is dropped
       is the "Total credit" subtotal, because it is the sum of the two legs
       standing next to it and the customer can see both. What is NOT dropped
       is the bonus: netting it into the balance is precisely the failure this
       pass exists to fix, so it keeps a leg of its own, with the word
       "guaranteed" attached to the number rather than to a footnote.
       The legs carry a plus sign because they ADD UP to the credit; only the
       balance at the end is a subtraction. */
    // FIXED, 29 Sep 2026 (review fix, minor): read the bar's OWN current
    // .is-open state before innerHTML rebuilds it. .is-open lives on `el`
    // (the persistent bar), not on the button innerHTML replaces every
    // render -- a re-render triggered while the disclosure is open (a price
    // settling via ug:prices:ready, a grade change) used to rebuild the
    // button hard-coded to the closed state (aria-expanded="false", label
    // "Details") even though .is-open (and so the CSS that actually shows
    // the legs) was still in effect, so the control's label/aria disagreed
    // with what was on screen.
    var dealMoreOpen = el.classList.contains('is-open');
    el.innerHTML =
      '<div class="ug-deal__inner">' +
        '<button type="button" class="ug-deal__close" aria-label="' + esc(T('deal.cancel_label')) + '" title="' + esc(plainText(T('deal.cancel_label'))) + '">&times;</button>' +
        // OBSIDIAN, 29 Sep 2026 (C8). Below 990px the bar is one row -- the
        // balance and the Order button -- with the legs, the caveats and
        // "Cancel this trade-in" behind this Details disclosure; from 990px
        // the CSS media query alone shows everything and hides this button.
        // This file carries no viewport-width check of its own: a class
        // toggle is the only state, and the stylesheet decides what it does
        // at each width.
        '<button type="button" class="ug-deal__more" data-ug-deal-more aria-expanded="' + (dealMoreOpen ? 'true' : 'false') + '">' +
          esc(dealMoreOpen ? T('deal.hide_details') : T('deal.details')) +
        '</button>' +
        '<div class="ug-deal__legs">' +
          '<span class="ug-deal__leg">' +
            '<span class="ug-deal__k">' + offerPillHtml + esc(targetName) + '</span>' +
            '<span class="ug-deal__v">' + fig(t.price) + '</span>' +
          '</span>' +
          // ug-deal__v--minus is the theme's "money coming back" colour hook
          // (volt as text). The name is left over from when this leg was a
          // deduction; the value it holds is now an addition to the credit.
          // Renaming it means editing the stylesheet, which is not this
          // unit's file — flagged in the hand-off instead.
          '<span class="ug-deal__leg">' +
            '<span class="ug-deal__k">' + esc(T('deal.your_device', { device: mineName })) + '</span>' +
            '<span class="ug-deal__v ug-deal__v--minus">' + fig(t.value, '+') + '</span>' +
          '</span>' +
          // The guarantee sits in the key, next to its own number: this is the
          // one figure on the bar that no inspection can move. The key is kept
          // short because .ug-deal__k ellipsises past 22ch.
          (t.bonus ? '<span class="ug-deal__leg ug-deal__leg--bonus">' +
            '<span class="ug-deal__k">' + esc(T('deal.bonus_guaranteed')) + '</span>' +
            '<span class="ug-deal__v">' + fig(t.bonus, '+') + '</span>' +
          '</span>' : '') +
        '</div>' +
        // R1: this is an estimate the inspection honours, not the amount the
        // checkout will charge. The old "You pay" said the opposite.
        // The caveat names WHICH part of the sum the inspection settles — the
        // phone's value — so it can no longer be read as covering the bonus.
        '<div class="ug-deal__pay">' +
          '<span class="ug-deal__k">' + esc(T('deal.estimated_balance')) + '</span>' +
          // t.covered: the credit is larger than the device's price, so there is
          // no balance to print. Same words as the trade-in result
          // (assets/upgrade-tradein.js) -- the bar and the result render in one
          // viewport on /pages/trade-in and must not describe the same deal two
          // different ways. This surface is not fed by tradeUpOptions(), so its
          // eligibility band cannot protect it: the target is pinned by
          // upgrade-pdp.js for whatever device the shopper opened. Measured
          // 24 Sep 2026: 30.23% of reachable target x valuation pairs are
          // covered, across 223 of 228 targets. Omar, 28 Sep 2026 (LOG-021): the
          // person at the door pays the customer the difference; allowed, rare.
          // --covered relaxes the nowrap money sizing this slot has for a figure;
          // without it the sentence is cut at the bar's edge on a phone and
          // collapses .ug-deal__legs to 0 on desktop, erasing the bonus leg.
          // F6, 29 Sep 2026 (fix_now, prices-on-page P7): t.surplus > 0 keeps
          // the existing sentence; an exact match (surplus === 0) has no
          // difference to pay, so it says "Nothing to pay" instead.
          (t.covered
            ? (t.surplus > 0
                ? '<strong class="ug-deal__total ug-deal__total--covered">' + esc(T('deal.covered_full')) + '</strong>'
                : '<strong class="ug-deal__total ug-deal__total--covered">' + esc(T('deal.covered_exact')) + '</strong>')
            : '<strong class="ug-deal__total">' + fig(t.pay) + ' <small>' + esc(T('common.currency')) + '</small></strong>') +
          '<span class="ug-deal__mo">' + esc(T('deal.value_confirmed')) + '</span>' +
          // valU (LOG-023) in place of the generic monthly line. Nothing to
          // finance on a covered deal. --fin lets the longer line wrap instead
          // of running past the bar's edge on a phone (upgrade-instalments.css).
          (t.covered ? '' : (function () {
            var v = valuPlan(t.price, t.pay);
            return v ? '<span class="ug-deal__mo ug-deal__mo--fin">' + esc(valuLine(v)) + '</span>' : '';
          })()) +
        '</div>' +
        ctaHtml() +
        // LF5 fix (29 Sep 2026, fix_now, audit w4k72rk5w): a refused order
        // used to say why only through `window.alert()` -- some in-app
        // webviews (and this build's own audit tooling) suppress native
        // dialogs, which left the button looking dead with no visible
        // explanation. This inline `role="status"` paragraph carries the
        // SAME wording `orderFailMessage()` always produced, plus a "View
        // cart" link, next to the button that was pressed -- starts hidden
        // and empty, filled only on a real order() rejection.
        '<p class="ug-deal__ordermsg" data-ug-deal-order-msg role="status" aria-live="polite" hidden></p>' +
      '</div>';

    // OBSIDIAN, 29 Sep 2026 (C8): the Details disclosure. One class, toggled
    // on click, nothing more -- publishHeight() re-measures the bar so the
    // page's own reserved foot space (html.ug-has-deal) grows and shrinks
    // with it instead of the expanded content sitting under the page.
    // Guarded like every other DOM lookup this function cannot assume a
    // caller supports (a stub whose querySelector only answers a fixed
    // allowlist and throws on anything else must not break render() for
    // the two controls it already knows about).
    try {
      var moreBtn = el.querySelector('[data-ug-deal-more]');
      if (moreBtn) {
        moreBtn.addEventListener('click', function () {
          var open = el.classList.toggle('is-open');
          moreBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
          moreBtn.textContent = open ? T('deal.hide_details') : T('deal.details');
          publishHeight();
        });
      }
    } catch (e) { /* a test stub modelling only a fixed set of selectors: below 990px the button stays a static "Details" label with nothing behind it, and desktop is unaffected either way */ }

    var orderBtn = el.querySelector('[data-ug-deal-order]');
    if (orderBtn) {
      orderBtn.addEventListener('click', function () {
        // LF5 fix: clear any earlier failure message before trying again.
        var msgEl = null;
        try { msgEl = el.querySelector('[data-ug-deal-order-msg]'); } catch (e) { msgEl = null; }
        if (msgEl) { msgEl.hidden = true; msgEl.textContent = ''; }
        orderBtn.disabled = true;
        orderBtn.textContent = T('deal.adding');
        order().catch(function (e) {
          orderBtn.disabled = false;
          orderBtn.textContent = ORDER_LABEL;
          if (msgEl) {
            msgEl.textContent = orderFailMessage(e) + ' ';
            var viewCart = document.createElement('a');
            viewCart.href = routeRoot() + 'cart';
            viewCart.textContent = T('deal.view_cart');
            msgEl.appendChild(viewCart);
            msgEl.hidden = false;
          }
        });
      });
    }

    // The × means cancel, not "hide until the next page load". Clearing the
    // stored deal is what actually makes it stay gone — hiding the element
    // alone left the state intact, so the bar rebuilt itself on every
    // navigation and the shopper could not get rid of it.
    el.querySelector('.ug-deal__close').addEventListener('click', function () {
      // LF7 fix (29 Sep 2026, fix_now, audit w4k72rk5w): API.clear() ->
      // hideBar() empties the bar's innerHTML, which removes the very
      // button that was just focused -- with nothing else claiming focus,
      // the browser drops it on <body>, and keyboard users lose their
      // place on the page entirely. Move focus to a stable target BEFORE
      // the button disappears: the page's own "Trade in for credit" CTA
      // when the page has one (keeps the shopper in the trade-in flow),
      // otherwise the page's <h1> (given tabindex="-1" if it lacks one,
      // so it is focusable without joining the tab order). No confirm, no
      // undo (RL6) -- the cancel itself is unchanged, only where focus
      // lands after it.
      focusStableTargetAfterCancel();
      API.clear();
      /* The one disappearance whose cause is known, so the one that is worth
         confirming. API.clear() -> announce() -> render() -> hideBar(), which
         has just emptied the region; writing on the NEXT tick, after that has
         run, is what makes the confirmation survive. Written before the clear
         it would be blanked and lost -- silently, and hard to spot in review.
         The wording matches this control's own aria-label, because the two are
         read one after the other. */
      setTimeout(function () {
        if (barStatus) { barStatus.textContent = T('deal.cancelled'); }
      }, 0);
    });

    el.hidden = false;
    document.documentElement.classList.add('ug-has-deal');
    // Publish synchronously on first paint: ResizeObserver's first callback is
    // a frame away, and for that frame the sticky buy bar would sit at 0 and be
    // covered -- which is the exact bug this is fixing.
    publishHeight();
    barWasShown = true;
    announceDeal(t, targetName, appeared);
  }

  /* The bar's one action (28 Sep 2026, LOG-021). A trade-in ends in an order,
     not a booking. With an orderable phone pinned -- the product page pins the
     Shopify variant its own Add to cart would use -- the button orders it with
     the trade-in written on it. Without one (a phone pinned from a trade-up
     card, or a grade that is out of stock) it sends them to the phone's page to
     choose, where Add to cart carries the trade-in the same way. */
  var ORDER_LABEL = T('deal.order_cta');

  function ctaHtml() {
    var tg = state.target || {};
    if (tg.variantId) {
      return '<button type="button" class="ug-btn ug-btn--primary ug-deal__cta" data-ug-deal-order>' + esc(ORDER_LABEL) + '</button>';
    }
    // Already on that phone's page (a sold-out grade, say): a link back to
    // this same page would only reload it. The page's own buttons choose.
    if (tg.url && String(location.pathname || '') === String(tg.url).split('?')[0]) return '';
    return '<a class="ug-btn ug-btn--primary ug-deal__cta" href="' + esc(targetHref(tg)) + '">' + linkLabel(tg) + '</a>';
  }

  /* Lynkrs PG-TradeIn-4 (6 Oct 2026, T2). The bar only ever shows with a
     phone pinned, so "Choose your phone" told a customer who had just pressed
     "Choose this" on a trade-up card to choose again. The link names the
     phone it opens instead -- "View iPhone 15" -- the same verb as the card's
     own "View this device". A target with no name (none is built today) keeps
     the old words. Returns HTML: the name is escaped.
     Arabic port (pA, 7 Oct 2026): the words are EN deal.view_device and
     deal.choose_phone; the model name stays as stored (Latin) and is isolated
     on a right-to-left page. English: esc('View ' + name) is exactly
     'View ' + esc(name), as before. */
  function linkLabel(tg) {
    var name = (tg && typeof tg.label === 'string') ? tg.label.trim() : '';
    return name ? esc(T('deal.view_device', { device: namePart(name) })) : esc(T('deal.choose_phone'));
  }

  function targetHref(tg) {
    // The phone's page, with the grade, storage and colour they chose, so it
    // opens on that exact phone rather than its default.
    if (tg.url) {
      // Set (not append) grade, storage and colour, so a url that already
      // carries a query -- the product page's own address, when its Shopify
      // product had not loaded -- still opens on the phone they chose.
      var parts = String(tg.url).split('#')[0].split('?');
      var keep = (parts[1] || '').split('&').filter(function (kv) {
        return kv && !/^(grade|storage|colour)=/i.test(kv);
      });
      if (tg.grade) keep.push('grade=' + encodeURIComponent(tg.grade));
      if (tg.storage) keep.push('storage=' + encodeURIComponent(tg.storage));
      if (tg.colour) keep.push('colour=' + encodeURIComponent(tg.colour));
      return parts[0] + (keep.length ? '?' + keep.join('&') : '');
    }
    // Lynkrs B6 (6 Oct 2026, T3): a phone pinned without a url (the trade-in
    // and sell trade-up cards pin none) opens its own product page,
    // /products/<handle>, from window.UpgradeRoutes, with the same grade,
    // storage and colour; the shop if Shopify has no product for it. It used
    // to open /pages/device?device=<series>.
    // Arabic port (pA, 7 Oct 2026): the address keeps the language prefix
    // (localHref(); routeRoot() for the shop). English: exactly as above.
    if (tg.series) {
      var routes = (typeof window !== 'undefined') ? window.UpgradeRoutes : null;
      return (routes && typeof routes.deviceHref === 'function')
        ? localHref(routes.deviceHref(tg.series, { grade: tg.grade, storage: tg.storage, colour: tg.colour }))
        : routeRoot() + 'pages/shop';
    }
    return routeRoot() + 'pages/shop';
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ---- finding the published data ----------------------------------------- */

  function selfSrc() {
    var me = document.querySelector('script[src*="upgrade-deal.js"]');
    return me ? me.getAttribute('src') : null;
  }

  /* Prefer a URL a section has already rendered with {{ ... | asset_url }} —
     it carries the correct CDN version stamp. Otherwise derive it from our own
     <script src>, since every theme asset is served from the same directory. */
  function dataUrl() {
    var el = document.querySelector('[data-upgrade-tradein], [data-upgrade-sell], [data-data-url], [data-json-url]');
    if (el) {
      var u = el.getAttribute('data-upgrade-tradein') || el.getAttribute('data-upgrade-sell') ||
              el.getAttribute('data-data-url') || el.getAttribute('data-json-url');
      if (u) return u;
    }
    var src = selfSrc();
    if (!src) return null;
    return src.split('?')[0].replace(/upgrade-deal\.js$/, 'upgrade-tradein-data.json');
  }

  function loadRates() {
    if (rates) return;
    var url = dataUrl();
    if (!url) {
      warn('cannot locate upgrade-tradein-data.json');
      return;
    }
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)); })
      .then(function (d) {
        if (!setRates(d && d.meta)) throw new Error('meta block publishes no bonus rate');
      })
      .catch(function (e) { warn(e && e.message ? e.message : String(e)); });
  }

  // Loud on purpose. With no published rate the bar stays hidden and the
  // booking summary stays empty; a silent blank is exactly how this class of
  // failure hid itself before.
  function warn(msg) {
    if (window.console && console.warn) {
      console.warn('[upgrade-deal] trade-up bonus rate unavailable, the deal bar will stay hidden: ' + msg);
    }
  }

  /* Load our stylesheet the same way core.js does, so this ships without
     touching a Liquid file. */
  (function loadCss() {
    var src = selfSrc();
    if (!src || document.querySelector('link[href*="upgrade-deal.css"]')) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = src.replace(/upgrade-deal\.js/, 'upgrade-deal.css');
    document.head.appendChild(link);
  })();

  /* ---- boot --------------------------------------------------------------- */

  /* 28 Sep 2026 REVIEW FINDING (D2 follow-up). The bar never repainted when
     the price map's walk finished. render() -> totals() -> syncedTarget()
     already re-reads window.UpgradePrices on every call, but nothing here
     called render() again once a map that started incomplete (above 50
     products, see snippets/upgrade-price-map.liquid's header) settled — the
     first paint kept whatever it read on load until some unrelated
     setTarget()/setTradein() happened to run later. The map's own
     "SYNCHRONOUS-READ RULE FOR CONSUMERS" asks every surface to render under
     whenReady() or re-render on ug:prices:ready; assets/upgrade-catalogue.js
     already does; this file did not. Dormant today (the live catalogue is
     under 50 products, so the seed is always complete) and goes live the day
     the catalogue crosses that line. whenReady() is synchronous and a no-op
     the moment the map is already settled (every page today), so this costs
     nothing on the common path. */
  function repaintWhenPricesReady() {
    document.addEventListener('ug:prices:ready', function () { render(); });
    var api = window.UpgradePrices;
    if (api && api.complete === false && typeof api.whenReady === 'function') {
      api.whenReady(function () { render(); });
    }
  }

  function boot() {
    var done = complete();
    if (done === 'order') {
      API.clear();
    } else if (done === 'booking') {
      var parked = state.attached;
      API.clear();
      if (parked) { state.attached = parked; write(); }
    }
    reconcileCart();
    if (!suppressed()) loadRates();
    repaintWhenPricesReady();
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
